import React, { useState, useMemo, useEffect, useRef } from 'react';
import OnboardingTooltip from './OnboardingTooltip';
import './LearnModeScreen.css';
import getLevenstein from 'fast-levenshtein';
import { supabase } from '../supabaseClient'; 
import { getUserRating, saveUserRating, deleteUserRating } from '../storageService';

const connectorWords = new Set(["a", "an", "the", "of", "to", "for", "on", "by", "with", "from", "in", "at", "and", "or", "but", "if", "because", "&", "is", "are", "was", "were", "has", "have", "had", "be", "being", "been", "generally", "usually", "often", "sometimes", "always", "never", "such", "that", "which", "who", "whom", "whose", "this", "these", "those", "there", "here", "it", "its", "they", "their", "them", "he", "she", "his", "her", "him", "my", "our", "us", "you", "made", "about", "as", "like", "just", "so", "than", "then", "now", "will", "shall", "can", "could", "would", "otherwise", "general"]);

const cleanText = (str) => { if (!str) return ''; return str.toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()?]/g, "").replace(/\s{2,}/g, " ").trim(); };

const checkBagOfWords = (targetPhrase, userText, threshold = 0.65) => {
    const filterTokens = (t) => cleanText(t).split(' ').filter(w => !connectorWords.has(w) && w.length > 0);
    const targetTokens = filterTokens(targetPhrase);
    const userTokens = filterTokens(userText);
    if (targetTokens.length === 0 || userTokens.length === 0) return false;
    let matchCount = 0;
    targetTokens.forEach(tWord => {
        const found = userTokens.some(uWord => { if (uWord === tWord) return true; if (tWord.length > 3 && getLevenstein.get(uWord, tWord) <= 2) return true; return false; });
        if (found) matchCount++;
    });
    return (matchCount / targetTokens.length) >= threshold;
};

const hasKeyConcept = (userText, conceptPhrase, strictness = 0.65) => checkBagOfWords(conceptPhrase, userText, strictness);

const areStringsSimilar = (strA, strB, threshold = 0.85) => {
    const normA = cleanText(strA); const normB = cleanText(strB);
    if (!normA && normB) return false; if (normA === normB) return true;
    const distance = getLevenstein.get(normA, normB); const longerLength = Math.max(normA.length, normB.length);
    const similarity = 1 - (distance / (longerLength || 1));
    if (similarity >= threshold) return true; return checkBagOfWords(strA, strB, threshold); 
};

const isContextualMatch = (userInput, target, contextBefore, contextAfter) => {
    if (!cleanText(userInput)) return false; if (areStringsSimilar(userInput, target)) return true;
    const userTokens = cleanText(userInput).split(/\s+/); const beforeTokens = cleanText(contextBefore).split(/\s+/);
    const afterTokens = cleanText(contextAfter).split(/\s+/); const targetTokens = cleanText(target).split(/\s+/);
    const checkTokens = (tokens) => areStringsSimilar(tokens.join(' '), target);
    if (afterTokens.length > 0 && userTokens.length > targetTokens.length) { if (userTokens[userTokens.length - 1] === afterTokens[0]) { const sub = userTokens.slice(0, -1); if (checkTokens(sub)) return true; } }
    if (beforeTokens.length > 0 && userTokens.length > targetTokens.length) { if (userTokens[0] === beforeTokens[beforeTokens.length - 1]) { const sub = userTokens.slice(1); if (checkTokens(sub)) return true; } }
    if (beforeTokens.length > 0 && afterTokens.length > 0 && userTokens.length > targetTokens.length + 1) { if (userTokens[0] === beforeTokens[beforeTokens.length - 1] && userTokens[userTokens.length - 1] === afterTokens[0]) { const sub = userTokens.slice(1, -1); if (checkTokens(sub)) return true; } }
    return false;
};

const blockOutWord = (word) => { let maskedWord = ''; for (const char of word) { if (/[a-zA-Z0-9]/.test(char)) maskedWord += '█'; else maskedWord += char; } return `<span class="cloze-placeholder">${maskedWord}</span>`; };

const generateProgressiveCloze = (clozeText, revealedIndices = new Set()) => {
  if (!clozeText) return '';
  const tokens = clozeText.split(' ');
  const hintTokens = tokens.map((token, index) => {
    if (!/[a-zA-Z0-9]/.test(token)) return token;
    const cleanToken = token.replace(/[.,/#!$%^&*;:{}=\-_`~()?]/g, "").toLowerCase();
    if (connectorWords.has(cleanToken) || revealedIndices.has(index)) { return `<span class="cloze-filled">${token}</span>`; }
    else { return blockOutWord(token); }
  });
  return hintTokens.join(' ');
};

function LearnModeScreen({ rule, onBack, onComplete, sessionProgress, sessionMode, onToggleFlag, onEditRule, onUpdateRuleKeynote, seenHints, onDismissHint }) {
  const [currentStep, setCurrentStep] = useState(sessionMode === 'refine' ? 3 : 1);
  const [userInput, setUserInput] = useState('');
  const strictness = 0.65;
  const [clozeIndex, setClozeIndex] = useState(0);
  const [clozeAnswers, setClozeAnswers] = useState([]);
  const [revealedIndices, setRevealedIndices] = useState(new Set());
  const [step3Result, setStep3Result] = useState(null);
  const [keynoteInput, setKeynoteInput] = useState('');
  const [isKeynoteSaved, setIsKeynoteSaved] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [hasSpeechSupport, setHasSpeechSupport] = useState(false);
  const [showError, setShowError] = useState(false);
  const recognitionRef = useRef(null);
  const shouldRestartRecognition = useRef(false);
const [rating, setRating] = useState(null);

  useEffect(() => { 
      const fetchRating = async () => {
          const { data: { user } } = await supabase.auth.getUser();
          if (user && rule) {
              const val = await getUserRating(user.id, rule.id);
              if (val === 1) setRating('up');
              else if (val === -1) setRating('down');
              else setRating(null);
          }
      };
      fetchRating();

      // Reset other state
      setCurrentStep(sessionMode === 'refine' ? 3 : 1); 
      setUserInput(''); 
      setClozeIndex(0); 
      setClozeAnswers([]); 
      setRevealedIndices(new Set()); 
      setStep3Result(null); 
      setKeynoteInput(rule?.keynote || ''); 
      setShowHint(false); 
      setInterimTranscript(''); 
      shouldRestartRecognition.current = false; 
      setShowError(false); 
      setIsKeynoteSaved(false); 
  }, [rule, sessionMode]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setHasSpeechSupport(true);
      const recognition = new SpeechRecognition(); recognition.continuous = true; recognition.interimResults = true; recognition.lang = 'en-US';
      recognition.onstart = () => { setIsListening(true); setInterimTranscript(''); };
      recognition.onend = () => { if (shouldRestartRecognition.current) { shouldRestartRecognition.current = false; setTimeout(() => { try { recognition.start(); } catch (e) { console.log("Restart error", e); } }, 50); } else { setIsListening(false); setInterimTranscript(''); } };
      recognition.onerror = (event) => { if (event.error === 'aborted') return; setIsListening(false); };
      recognition.onresult = (event) => {
        let newFinal = ''; let newInterim = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) { if (event.results[i].isFinal) newFinal += event.results[i][0].transcript; else newInterim += event.results[i][0].transcript; }
        if (newFinal) { setUserInput(prev => { const spacer = (prev.length > 0 && !prev.endsWith(' ') && !newFinal.startsWith(' ')) ? ' ' : ''; return prev + spacer + newFinal; }); }
        setInterimTranscript(newInterim);
      };
      recognitionRef.current = recognition;
    }
  }, []);

  const toggleMicrophone = () => { if (!recognitionRef.current) return; if (isListening) { shouldRestartRecognition.current = false; recognitionRef.current.stop(); } else { recognitionRef.current.start(); } };

  const { clozesData, fullText, clozes } = useMemo(() => {
    if (!rule) return { clozesData: [], fullText: '', clozes: [] };
    const cleanTextWithBrackets = rule.content_html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const matches = [...cleanTextWithBrackets.matchAll(/\[(.*?)\]/g)];
    const data = matches.map((m, i) => { const answer = m[1].trim(); const index = m.index; const fullMatchStr = m[0]; const textBefore = cleanTextWithBrackets.substring(0, index).trim(); const wordsBefore = textBefore.split(' '); const contextBefore = wordsBefore.slice(-3).join(' '); const textAfter = cleanTextWithBrackets.substring(index + fullMatchStr.length).trim(); const wordsAfter = textAfter.split(' '); const contextAfter = wordsAfter.slice(0, 3).join(' '); return { answer, contextBefore, contextAfter }; });
    return { clozesData: data, fullText: cleanTextWithBrackets.replace(/\[|\]/g, ''), clozes: data.map(d => d.answer) };
  }, [rule]);

   const step2Html = useMemo(() => {
      let clozeCounter = -1;
      return rule.content_html.replace(/\[(.*?)\]/g, (match, inner) => {
          clozeCounter++;
          if (clozeCounter < clozeIndex) return `<span class="cloze-filled">${clozeAnswers[clozeCounter]}</span>`;
          if (clozeCounter > clozeIndex) return `<strong class="cloze-revealed">${inner}</strong>`;
          return `<span class="cloze-active">${generateProgressiveCloze(clozes[clozeIndex], revealedIndices)}<sup class="cloze-word-count">${clozes[clozeIndex].split(/\s+/).length}</sup></span>`;
      });
  }, [rule, clozeIndex, clozeAnswers, revealedIndices, clozes]);

  const step3PromptHtml = useMemo(() => { if (!rule) return ''; return rule.content_html.replace(/\[(.*?)\]/g, (match, inner) => { const rawText = inner.replace(/<[^>]+>/g, ''); const hint = generateProgressiveCloze(rawText); const wordCount = rawText.trim().split(/\s+/).length; return `<span class="cloze-active">${hint}<sup class="cloze-word-count">${wordCount}</sup></span>`; }); }, [rule]);

  const blurredHintHtml = useMemo(() => rule.content_html.replace(/\[(.*?)\]/g, (match, innerText) => `<span class="blurred-text">${innerText}</span>`), [rule]);

  const correctRuleHtml = useMemo(() => {
    if (!rule || !rule.content_html) return '';
    return rule.content_html.replace(/\[(.*?)\]/g, '<strong class="cloze-revealed">$1</strong>');
  }, [rule]);

  const highlightedAttemptHtml = useMemo(() => {
    if (!step3Result) return '';
    const foundConcepts = step3Result.results.filter(r => r.found).map(r => r.concept);
    const filterTokens = (t) => cleanText(t).split(' ').filter(w => !connectorWords.has(w) && w.length > 0);
    const significantWords = new Set();
    foundConcepts.forEach(concept => { filterTokens(concept).forEach(token => significantWords.add(token)); });
    if (significantWords.size === 0) return step3Result.userText;
    const userParts = step3Result.userText.split(/(\s+)/);
    const highlightedParts = userParts.map(part => {
        const cleanPart = cleanText(part);
        if (!cleanPart) return part;
        let isMatch = false;
        for (const sigWord of significantWords) {
            if (cleanPart === sigWord || (sigWord.length > 3 && getLevenstein.get(cleanPart, sigWord) <= 2)) { isMatch = true; break; }
        }
        if (isMatch) { return `<span class="highlighted-term">${part}</span>`; }
        return part;
    });
    return highlightedParts.join('');
  }, [step3Result]);

  const triggerError = () => { setShowError(true); setTimeout(() => setShowError(false), 800); };

  const getNextHiddenWordIndex = (tokens, revealedSet) => { for (let i = 0; i < tokens.length; i++) { if (revealedSet.has(i)) continue; const clean = cleanText(tokens[i]); if (connectorWords.has(clean)) continue; return i; } return -1; };

  const scanInputForMatches = (fullInputVal, currentRevealedSet, currentClozeString) => {
      const inputTokens = fullInputVal.trim().split(/\s+/); const clozeTokens = currentClozeString.split(' '); const newRevealedSet = new Set(currentRevealedSet);
      for (const inputToken of inputTokens) { const cleanInput = cleanText(inputToken); if (!cleanInput) continue; const targetIndex = getNextHiddenWordIndex(clozeTokens, newRevealedSet); if (targetIndex === -1) break; const targetWord = clozeTokens[targetIndex]; const cleanTarget = cleanText(targetWord); const dist = getLevenstein.get(cleanInput, cleanTarget); if (cleanInput === cleanTarget || (cleanTarget.length > 3 && dist <= 2)) newRevealedSet.add(targetIndex); }
      return { hasChanged: newRevealedSet.size > currentRevealedSet.size, newSet: newRevealedSet };
  };

  const handleInputChange = (e) => { const val = e.target.value; if (currentStep === 2 && val.endsWith(' ')) { const { hasChanged, newSet } = scanInputForMatches(val, revealedIndices, clozes[clozeIndex]); if (hasChanged) setRevealedIndices(newSet); } setUserInput(val); };
  
  const handleRating = async (newRating) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const nextRating = rating === newRating ? null : newRating;
      setRating(nextRating); // Optimistic UI update

      if (nextRating) {
          const val = nextRating === 'up' ? 1 : -1;
          await saveUserRating(user.id, rule.id, 'rule', val);
      } else {
          await deleteUserRating(user.id, rule.id);
      }
  };

  const handleNextStep = () => {
    setShowHint(false);
    if (currentStep === 1) { if (isListening && recognitionRef.current) { shouldRestartRecognition.current = false; recognitionRef.current.stop(); } setCurrentStep(2); setUserInput(''); return; }
    if (currentStep === 2) {
        const finalInput = userInput + (userInput && !userInput.endsWith(' ') && interimTranscript ? ' ' : '') + interimTranscript;
        if (getNextHiddenWordIndex(clozes[clozeIndex].split(' '), revealedIndices) === -1) { setClozeAnswers(prev => [...prev, clozesData[clozeIndex].answer]); setRevealedIndices(new Set()); setUserInput(''); setInterimTranscript(''); if (clozeIndex + 1 < clozes.length) setClozeIndex(prev => prev + 1); else setCurrentStep(3); return; }
        if (!cleanText(finalInput)) { triggerError(); return; }
        if (isContextualMatch(finalInput, clozesData[clozeIndex].answer, clozesData[clozeIndex].contextBefore, clozesData[clozeIndex].contextAfter)) { setClozeAnswers(prev => [...prev, clozesData[clozeIndex].answer]); setUserInput(''); setInterimTranscript(''); setRevealedIndices(new Set()); if (clozeIndex + 1 < clozes.length) { if (isListening && recognitionRef.current) { shouldRestartRecognition.current = true; recognitionRef.current.abort(); } setClozeIndex(prev => prev + 1); } else { if (isListening && recognitionRef.current) { shouldRestartRecognition.current = false; recognitionRef.current.stop(); } setCurrentStep(3); } } else { triggerError(); }
        return;
    }
    if (currentStep === 3) {
        if (isListening && recognitionRef.current) { shouldRestartRecognition.current = false; recognitionRef.current.stop(); }
        const finalInput = userInput + (userInput && !userInput.endsWith(' ') && interimTranscript ? ' ' : '') + interimTranscript;
        if (!cleanText(finalInput)) { triggerError(); return; }
        const results = clozes.map(concept => ({ concept, found: hasKeyConcept(finalInput, concept, strictness) }));
        setStep3Result({ missedCount: results.filter(r => !r.found).length, results, userText: finalInput });
        return;
    }
  };

  const handleSaveKeynote = () => { if (!isDirty) return; onUpdateRuleKeynote(rule.id, keynoteInput); setIsKeynoteSaved(true); };
  const handleFinish = () => onComplete(rule.id, null, null, null);

  const isDirty = useMemo(() => keynoteInput !== (rule.keynote || ''), [keynoteInput, rule.keynote]);
  const isFinishDisabled = useMemo(() => (isDirty && !isKeynoteSaved) || (!isDirty && !rule.keynote), [isDirty, isKeynoteSaved, rule.keynote]);

  const RatingWidget = () => (
    <div className="rating-widget">
        <div className="rating-label-wrapper">
            <span className="rating-label">Rate:</span>
            <div className="tooltip">
                <span className="material-symbols-outlined icon-sm help-icon">help_outline</span>
                <div className="tooltip-text-rating">
                    Rating this rule helps our team identify content that needs improvement, making it better for everyone.<br />
                    <span className="beta-badge">BETA</span>
                </div>
            </div>
        </div>
        <div className="rating-buttons">
            <button className={`rate-btn ${rating === 'up' ? 'active' : ''}`} onClick={() => handleRating('up')}><span className="material-symbols-outlined">arrow_upward</span></button>
            <button className={`rate-btn ${rating === 'down' ? 'active' : ''}`} onClick={() => handleRating('down')}><span className="material-symbols-outlined">arrow_downward</span></button>
        </div>
    </div>
  );

  const handleDebugAutofill = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      const allAnswers = clozesData.map(d => d.answer);
      setClozeAnswers(allAnswers);
      setClozeIndex(allAnswers.length);
      setCurrentStep(3);
      setUserInput('');
    } else if (currentStep === 3 && !step3Result) {
      const results = clozes.map(concept => ({ concept, found: true }));
      setStep3Result({ missedCount: 0, results, userText: fullText });
    } else if (step3Result) {
      setStep3Result(null);
      setCurrentStep(4);
      setKeynoteInput(fullText);
    } else if (currentStep === 4) {
      setKeynoteInput(fullText);
      onUpdateRuleKeynote(rule.id, fullText);
      setIsKeynoteSaved(true);
    }
  };

  return (
    <div className="learn-mode-container theme-aware">
      <header className="learn-mode-header">
        <button onClick={onBack} className="learn-mode-back-btn">←</button>
        {process.env.NODE_ENV === 'development' && (<button onClick={handleDebugAutofill} style={{ background: '#ff4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '0.7rem', fontWeight: 'bold', cursor: 'pointer', marginLeft: '10px' }}>DEBUG AUTOFILL</button>)}
        <div className="header-center-info">{sessionProgress && <span className="session-progress">Batch: {sessionProgress.current + 1} of {sessionProgress.total}</span>}</div>
      </header>
      <main className="learn-mode-content-area">
        <div className="rule-breadcrumbs centered-breadcrumbs">{(rule.displayPath || []).join(' / ')}</div>
        <h2 className="rule-title centered-title">{rule.ruleName}</h2>
        <div className="card-container learn-content">
            {currentStep === 1 && (
                <div className="step-container">
                    <p className="step-instruction"><strong>Step 1:</strong> Read the rule aloud.</p>
                    <div className="rule-display-text" dangerouslySetInnerHTML={{ __html: correctRuleHtml }} />
                    <div className="input-wrapper">
                        <OnboardingTooltip id="hint_learn_cloze" seenHints={seenHints} onDismiss={onDismissHint} position="top" text="Type the exact legal term to unlock the next word and reveal the hint.">
                            <input type="text" className="learn-input optional-input" placeholder="Type to practice or Press Enter to skip..." value={userInput} onChange={(e) => setUserInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleNextStep()} autoFocus />
                        </OnboardingTooltip>
                    </div>
                </div>
            )}
            {currentStep === 2 && (
                <div className="step-container">
                    <p className="step-instruction"><strong>Step 2:</strong> Fill in the phrase.</p>
                    {showHint ? <div className="rule-display-text hint-overlay-container"><div className="hint-header-row"><span className="material-symbols-outlined icon-sm">lightbulb</span><span>Hover text to reveal</span></div><div className="hint-section"><span className="hint-label">Full Rule:</span><div className="blurred-text-html" dangerouslySetInnerHTML={{ __html: blurredHintHtml }} /></div></div> : <div className="rule-display-text" dangerouslySetInnerHTML={{ __html: step2Html }} />}
                    <div className="input-wrapper">
                        <input type="text" className={`learn-input ${showError ? 'input-error' : ''}`} value={userInput} onChange={handleInputChange} onKeyDown={(e) => e.key === 'Enter' && handleNextStep()} autoFocus placeholder="Type the hidden term." />
                        {hasSpeechSupport && <button className={`mic-btn ${isListening ? 'listening' : ''}`} onClick={toggleMicrophone}><span className="material-symbols-outlined">{isListening ? 'mic' : 'mic_off'}</span></button>}
                    </div>
                </div>
            )}
            {currentStep === 3 && !step3Result && (
                <div className="step-container">
                    <p className="step-instruction"><strong>Step 3:</strong> Recall the full rule.</p>
                    {showHint ? <div className="rule-display-text hint-overlay-container"><div className="hint-header-row"><span className="material-symbols-outlined icon-sm">lightbulb</span><span>Hover text to reveal</span></div><div className="hint-section"><span className="hint-label">Full Rule:</span><div className="blurred-text-html" dangerouslySetInnerHTML={{ __html: blurredHintHtml }} /></div></div> : <div className="rule-display-text" dangerouslySetInnerHTML={{ __html: step3PromptHtml }} />}
                    <div className="input-wrapper"><textarea className={`learn-input ${showError ? 'input-error' : ''}`} value={userInput} onChange={(e) => setUserInput(e.target.value)} onKeyDown={(e) => (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) && handleNextStep()} rows="4" autoFocus placeholder="Type the full rule..." />{hasSpeechSupport && <button className={`mic-btn ${isListening ? 'listening' : ''}`} onClick={toggleMicrophone}><span className="material-symbols-outlined">{isListening ? 'mic' : 'mic_off'}</span></button>}</div>
                </div>
            )}
            {step3Result && (
                <div className="step-container">
                    <h3 className={`result-title ${step3Result.missedCount === 0 ? 'success' : ''}`}>
                        <img 
                            src={step3Result.missedCount === 0 ? "/mascot/logoPortraitFolded.png" : "/mascot/logoDeNovo.png"} 
                            alt="Wali Result" 
                            className="result-title-icon" 
                        />
                        {step3Result.missedCount === 0 ? "Perfect Recall!" : "Check missed elements."}
                    </h3>
                    <div className="comparison-grid">
                        <div className="comparison-col">
                            <span className="col-label">Correct Rule</span>
                            <div className="rule-text small" dangerouslySetInnerHTML={{ __html: correctRuleHtml }} />
                        </div>
                        <div className="comparison-col">
                            <span className="col-label">Your Attempt</span>
                            <div className="rule-text small attempt-text" dangerouslySetInnerHTML={{ __html: highlightedAttemptHtml }} />
                        </div>
                    </div>
                    <div className="element-checklist">
                        {step3Result.results.map((res, i) => (
                            <div key={i} className={`check-item ${res.found ? 'found' : 'missed'}`}>
                                <span className="material-symbols-outlined">{res.found ? 'check_circle' : 'cancel'}</span>
                                {res.found ? res.concept : <del>{res.concept}</del>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {currentStep === 4 && (
                <div className="step-container">
                    <p className="step-instruction"><strong>Step 4:</strong> Create a Keynote.</p>
                    <div className="rule-display-wrapper">
                        <div className="rule-display-text" style={{fontSize:'1em', opacity:0.8}} dangerouslySetInnerHTML={{ __html: correctRuleHtml }} />
                        <RatingWidget />
                    </div>
                    <textarea className="learn-input" placeholder="Add a personal mnemonic or note..." value={keynoteInput} onChange={(e) => { setKeynoteInput(e.target.value); setIsKeynoteSaved(false); }} autoFocus />
                </div>
            )}
            <div className="learn-actions centered">
                {(currentStep === 2 || (currentStep === 3 && !step3Result)) && <button className="btn btn-secondary hint-btn" onClick={() => setShowHint(!showHint)}>{showHint ? "Hide Hint" : "Hint"}</button>}
                {step3Result ? (<><button className="btn btn-secondary" onClick={() => { setStep3Result(null); setUserInput(''); }}>Retry</button><button className="btn" onClick={() => { setStep3Result(null); setCurrentStep(4); }}>Continue</button></>) : currentStep === 4 ? (<><button className="btn" onClick={handleSaveKeynote} disabled={!isDirty || isKeynoteSaved}>{isKeynoteSaved ? 'Saved' : 'Save Keynote'}</button><button className="btn" onClick={handleFinish} disabled={isFinishDisabled}>Finish</button></>) : (<button className="btn" onClick={handleNextStep}>{currentStep === 3 ? "Check Answer" : "Next Step"}</button>)}
            </div>
        </div>
      </main>
    </div>
  );
}

export default LearnModeScreen;